import { format } from 'date-fns'
import * as React from 'react'
import { useRef, useState } from 'react'
import styled from 'styled-components'
import { ElementWithTooltip } from '../../../../element-with/element-with-tooltip/ElementWithTooltip'
import { CustomDateCalendarStyled } from '../../../styled/date-range-selector/styled'
import { InputStyled } from '../../../styled/date-range/styled'
import { ErrorMsgStyled, LabelStyled, ResetStyled, SearchButtonStyled, WidgetHeadingStyled } from '../../../styled/side-panel/styled'
import { CustomDatesCalendarDO } from '../../types'

const DateInputWrapper = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`

const CalendarIconButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0 4px;
    color: ${(props) => props.theme.colors.dark[1]};
    opacity: 0.7;
    &:hover {
        opacity: 1;
    }
`

const HiddenDatePicker = styled.input`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
`

const DATE_PLACEHOLDER = 'YYYY-MM-DD'

export default function CustomDatesCalendar({ code, field, handlers, setShow }: CustomDatesCalendarDO) {
    const [className] = useState('CustomDatesCalendar-' + new Date().getTime())
    const [noSelection, setNoSelection] = useState<boolean>(false)
    const [wrongDates, setWrongDates] = useState<boolean>(false)
    const [isCustomRangeOpen, setIsCustomRangeOpen] = useState<boolean>(false)
    const [fromDateValue, setFromDateValue] = useState<string>('')
    const [toDateValue, setToDateValue] = useState<string>('')
    const fromPickerRef = useRef<HTMLInputElement>(null)
    const toPickerRef = useRef<HTMLInputElement>(null)
    const hasPast = field.hasPast
    const hasFuture = field.hasFuture

    // https://date-fns.org/v2.29.3/docs/format
    let minMaxFormat: string = 'y-MM-dd'
    const todayForMinMax: string = format(new Date(), minMaxFormat)

    /*HANDLERS*/
    const setCurrentUnit = handlers['setCurrentUnit']
    const setCurrentNum = handlers['setCurrentNum']
    const setCustomDatesSelected = handlers['setCustomDatesSelected']
    const dispatchRangeQueryHandler = handlers['dispatchRangeQueryHandler']

    function selectDateRangeHandler() {
        const fromDate = fromDateValue
        const toDate = toDateValue

        if (!fromDate || !toDate) {
            setNoSelection(true)
            return
        }

        if (fromDate && toDate && fromDate > toDate) {
            setWrongDates(true)
            return
        }
        setWrongDates(false)
        setNoSelection(false)
        setCurrentUnit('')
        setCurrentNum(0)

        const toDt = new Date(toDate)
        toDt.setHours(23, 59, 59, 9999)
        dispatchRangeQueryHandler({
            from: new Date(fromDate),
            to: toDt,
            unit: '',
        })
        setCustomDatesSelected(true)
        setShow(false)
    }

    function resetDateRangeHandler() {
        setFromDateValue('')
        setToDateValue('')
        setCustomDatesSelected(false)
        setCurrentUnit('')
        setCurrentNum(0)
        setWrongDates(false)
        setNoSelection(false)
    }

    function handleFromTextChange(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value
        setFromDateValue(raw)
        setNoSelection(false)
        setWrongDates(false)
    }

    function handleToTextChange(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value
        setToDateValue(raw)
        setNoSelection(false)
        setWrongDates(false)
    }

    function handleFromPickerChange(e: React.ChangeEvent<HTMLInputElement>) {
        setFromDateValue(e.target.value)
        setNoSelection(false)
        setWrongDates(false)
    }

    function handleToPickerChange(e: React.ChangeEvent<HTMLInputElement>) {
        setToDateValue(e.target.value)
        setNoSelection(false)
        setWrongDates(false)
    }

    function openFromPicker() {
        fromPickerRef.current?.showPicker?.()
        fromPickerRef.current?.click()
    }

    function openToPicker() {
        toPickerRef.current?.showPicker?.()
        toPickerRef.current?.click()
    }

    return (
        <>
            <CustomDateCalendarStyled>
                <div className="col-md-3">
                    <WidgetHeadingStyled active={isCustomRangeOpen} extraClass={'px-2'}>
                        <span>Custom Dates</span> <br />
                        <ElementWithTooltip
                            element={<ResetStyled onClick={resetDateRangeHandler}>Reset</ResetStyled>}
                            tooltip={<span>Reset dates.</span>}
                        />
                    </WidgetHeadingStyled>
                </div>
                <div className="col-md-9">
                    <div className="row g-0 d-flex justify-content-end align-items-center">
                        <div className={'col'}>
                            <LabelStyled htmlFor={'fromDate'} fontSize={'12px'}>
                                {'From'}{' '}
                            </LabelStyled>
                            <br />
                            <DateInputWrapper>
                                <InputStyled
                                    id={'fromDateRef'}
                                    name={'fromDateRef'}
                                    type="text"
                                    placeholder={DATE_PLACEHOLDER}
                                    value={fromDateValue}
                                    onChange={handleFromTextChange}
                                />
                                <CalendarIconButton type="button" onClick={openFromPicker} title="Pick date">
                                    <i className={'fa-regular fa-calendar-days small'} />
                                </CalendarIconButton>
                                <HiddenDatePicker
                                    ref={fromPickerRef}
                                    type="date"
                                    value={fromDateValue}
                                    min={hasPast ? undefined : todayForMinMax}
                                    max={hasFuture ? undefined : todayForMinMax}
                                    onChange={handleFromPickerChange}
                                    tabIndex={-1}
                                />
                            </DateInputWrapper>
                        </div>
                        <div className={'col'}>
                            <LabelStyled htmlFor={'toDate'} fontSize={'12px'}>
                                {'To'}
                            </LabelStyled>
                            <br />
                            <DateInputWrapper>
                                <InputStyled
                                    id={'toDate'}
                                    name={'toDate'}
                                    type="text"
                                    placeholder={DATE_PLACEHOLDER}
                                    value={toDateValue}
                                    onChange={handleToTextChange}
                                />
                                <CalendarIconButton type="button" onClick={openToPicker} title="Pick date">
                                    <i className={'fa-regular fa-calendar-days small'} />
                                </CalendarIconButton>
                                <HiddenDatePicker
                                    ref={toPickerRef}
                                    type="date"
                                    value={toDateValue}
                                    min={hasPast ? undefined : todayForMinMax}
                                    max={hasFuture ? undefined : todayForMinMax}
                                    onChange={handleToPickerChange}
                                    tabIndex={-1}
                                />
                            </DateInputWrapper>
                        </div>
                        <div className="col text-center">
                            <ElementWithTooltip
                                element={
                                    <SearchButtonStyled onClick={selectDateRangeHandler} notBordered notRounded>
                                        Search
                                    </SearchButtonStyled>
                                }
                                tooltip={<span>Search within selected date range.</span>}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-md-12 text-center">
                    {noSelection && <ErrorMsgStyled>Please, select From and To dates!</ErrorMsgStyled>}
                    {wrongDates && <ErrorMsgStyled>Start date is greater then end date!</ErrorMsgStyled>}
                </div>
            </CustomDateCalendarStyled>
        </>
    )
}
